/**
 * <ErrorState> — F044 §9.1. The F043/F044 error format: separates UI availability from governance
 * continuity ("Policy evaluation remains active... last verified enforcement action: 11s ago"). Never
 * shows a raw HTTP status code. Retry is shown only when it can plausibly succeed (not on a 4xx).
 *
 * Named distinctly from `src/components/list/states.tsx`'s `<ErrorState>` (a generic list-loading
 * error) — this component owns the F044 governance-continuity contract specifically; import it from
 * `src/components/verdict`, not `src/components/list`.
 */
import type { FC } from 'react';
import { Body1, Button, Caption1, Title3, makeStyles, tokens } from '@fluentui/react-components';
import { ErrorCircleRegular, ShieldCheckmarkRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  errorIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorPaletteRedForeground1,
  },
  continuity: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  continuityIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorPaletteGreenForeground1,
  },
});

/** Mirrors the API's `GovernanceContinuityDto` shape (F043 §4.5) — kept local to verdict-kit rather
 *  than imported from a not-yet-built F043 Portal type module. */
export interface GovernanceContinuityInfo {
  policyEvaluationActive: boolean;
  evidenceCollectionActive: boolean;
  enforcementInterruptionDetected: boolean;
  lastVerifiedEnforcementAction?: string;
  cause: string;
  engineeringStatus: string;
}

export interface ErrorStateProps {
  /** A human cause — NEVER a raw HTTP status code. */
  cause: string;
  continuity?: GovernanceContinuityInfo;
  /** Whether a retry can plausibly succeed (e.g. transient/5xx) — never shown for a 4xx-shaped failure. */
  retryable?: boolean;
  onRetry?: () => void;
}

export const ErrorState: FC<ErrorStateProps> = ({ cause, continuity, retryable = false, onRetry }) => {
  const styles = useStyles();

  return (
    <div className={styles.root} role="alert">
      <div className={styles.header}>
        <ErrorCircleRegular className={styles.errorIcon} />
        <Title3>Unable to Load This View</Title3>
      </div>
      <Body1>{cause}</Body1>

      {continuity && (
        <div className={styles.continuity}>
          {continuity.policyEvaluationActive && !continuity.enforcementInterruptionDetected ? (
            <ShieldCheckmarkRegular className={styles.continuityIcon} />
          ) : (
            <ErrorCircleRegular className={styles.errorIcon} />
          )}
          <div>
            <Body1>
              {continuity.policyEvaluationActive
                ? 'Policy evaluation remains active.'
                : 'Policy evaluation is not currently active.'}
              {continuity.lastVerifiedEnforcementAction &&
                ` Last verified enforcement action: ${continuity.lastVerifiedEnforcementAction}.`}
            </Body1>
            <Caption1>{continuity.engineeringStatus}</Caption1>
          </div>
        </div>
      )}

      {retryable && onRetry && (
        <Button appearance="secondary" onClick={onRetry}>
          Try Again
        </Button>
      )}
    </div>
  );
};
